import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import convertS3ToCloudFront from "../../../../../utils/convertS3ToCloudFront";

const generateAnnexureExcel = async (
  invoiceData,
  uploadToS3 = false,
  apiBaseUrl = null
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Invoice Annexure");

  // Title
  worksheet.mergeCells("A1:L1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "As per Annexure";
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };

  // Headers
  const headers = [
    "Sr. No.",
    "Location",
    "Media Vehicle",
    "Size (W)",
    "Size (H)",
    "Sq Ft",
    "Media Type",
    "Start Date",
    "End Date",
    "Billing Amount",
    "Printing Charge",
    "Mounting Charge",
  ];
  const headerRow = worksheet.addRow(headers);

  // Header styling
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Data rows
  const billedInvs = invoiceData?.billedInvs || [];
  let totalAmount = 0;

  billedInvs.forEach((item, index) => {
    const width = Number(item.width) || 0;
    const height = Number(item.height) || 0;
    const sqFt = width * height;
    const amount = Number(item.amount) || 0;
    totalAmount += amount;

    const row = worksheet.addRow([
      index + 1,
      item.locationName || "",
      item.mediaVehical || "",
      width,
      height,
      sqFt,
      item.mediaType || "",
      item.startDate ? moment(item.startDate).format("DD-MM-YYYY") : "",
      item.endDate ? moment(item.endDate).format("DD-MM-YYYY") : "",
      amount.toFixed(2),
      "", // printing
      "", // mounting
    ]);

    row.eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
  });

  // Merge cells for printing & mounting
  const startRow = headerRow.number + 1;
  const endRow = worksheet.lastRow.number;
  worksheet.mergeCells(`K${startRow}:K${endRow}`);
  worksheet.mergeCells(`L${startRow}:L${endRow}`);

  const printingCharge = parseInt(invoiceData.printingCharge) || 0;
  const mountingCharge = parseInt(invoiceData.mountingCharge) || 0;

  const printingCell = worksheet.getCell(`K${startRow}`);
  printingCell.value = printingCharge;
  printingCell.alignment = { horizontal: "center", vertical: "middle" };
  printingCell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  const mountingCell = worksheet.getCell(`L${startRow}`);
  mountingCell.value = mountingCharge;
  mountingCell.alignment = { horizontal: "center", vertical: "middle" };
  mountingCell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  // Total row
  const totalRow = worksheet.addRow([
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "Total",
    totalAmount.toFixed(2),
    printingCharge,
    mountingCharge,
  ]);
  totalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF7F7F7" }, // very light gray
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Grand total row (merged across J–L)
  const grandTotal = totalAmount + printingCharge + mountingCharge;
  const grandTotalRow = worksheet.addRow([
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ]);

  const grandTotalRowIndex = grandTotalRow.number;
  worksheet.mergeCells(`J${grandTotalRowIndex}:L${grandTotalRowIndex}`);
  const grandTotalCell = worksheet.getCell(`J${grandTotalRowIndex}`);
  grandTotalCell.value = `Grand Total: ${grandTotal.toFixed(2)}`;
  grandTotalCell.font = { bold: true, size: 12 };
  grandTotalCell.alignment = { horizontal: "center", vertical: "middle" };
  grandTotalCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFECECEC" }, // medium grey
  };
  grandTotalCell.border = {
    top: { style: "medium" },
    left: { style: "medium" },
    bottom: { style: "medium" },
    right: { style: "medium" },
  };

  // Column widths
  worksheet.getColumn(1).width = 7;
  worksheet.getColumn(2).width = 70;
  worksheet.columns.forEach((column, index) => {
    if (index !== 0 && index !== 1) column.width = 15;
  });

  // Export
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Get the party name (using alias if available, otherwise parent company name)
  const partyName =
    invoiceData?.partyName ||
    invoiceData?.aliasPartyName ||
    invoiceData?.splitParentCompanyName ||
    invoiceData?.parentCompanyName;

  // Get the bill number
  const billNumber = invoiceData?.billNumber;

  // Get the brand name
  const brandName = invoiceData?.brandName;

  // Prepare final file name with safe fallbacks
  const fileName = `${billNumber}_${partyName}_${brandName}__Annexure.xlsx`;

  // If uploadToS3 is true, upload to S3 and return the URL
  if (uploadToS3 && apiBaseUrl) {
    try {
      // Get upload policy from API
      const requestBody = {
        fileName: fileName,
        mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };

      const response = await fetch(`${apiBaseUrl}/chats/uploadPolicy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${window.localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result?.data?.url && result?.data?.fields) {
        const formData = new FormData();

        // Ensure Content-Type is set correctly for S3 policy
        const expectedContentType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        // Add all fields from the upload policy
        for (const [key, value] of Object.entries(result.data.fields)) {
          // Override Content-Type if present to ensure exact match
          if (key.toLowerCase() === "content-type") {
            formData.set(key, expectedContentType);
          } else {
            formData.set(key, value);
          }
        }

        // Ensure Content-Type is set even if not in fields
        if (!formData.has("Content-Type") && !formData.has("content-type")) {
          formData.set("Content-Type", expectedContentType);
        }

        // Add the actual Excel file
        formData.append("file", blob, fileName);

        // Upload to S3
        const uploadResponse = await fetch(result.data.url, {
          method: "POST",
          body: formData,
        });

        if (uploadResponse.ok) {
          // Construct the final URL
          const finalUrl = convertS3ToCloudFront(
            `${result.data.url}/${result.filePath}`,
          );
          return finalUrl; // Return the S3 URL
        } else {
          throw new Error("Excel upload failed");
        }
      } else {
        throw new Error("Missing required data in upload response");
      }
    } catch (error) {
      console.error("Error uploading Excel:", error);
      throw error; // Re-throw to handle in calling function
    }
  } else {
    // Default behavior: download the file
    saveAs(blob, fileName);
    return null; // No URL to return for download
  }
};

export default generateAnnexureExcel;
